'use client';

import { useId } from 'react';
import { cn } from '@/lib/utils';

const MAX_STARS = 6;

type EmissionStarsProps = {
  rating: number;
  className?: string;
};

export const EmissionStars = ({ rating, className }: EmissionStarsProps) => {
  const id = useId();

  return (
    <div
      className={cn('inline-flex', className)}
      role="img"
      aria-label={`Emission rating - ${rating} out of ${MAX_STARS} stars`}
    >
      {Array.from({ length: MAX_STARS }, (_, i) => {
        const filled = i < Math.floor(rating);
        const halfFilled = !filled && i < rating;
        return (
          <StarIcon
            key={i}
            filled={filled}
            halfFilled={halfFilled}
            clipId={halfFilled ? `${id}-half-${i}` : undefined}
          />
        );
      })}
    </div>
  );
};

const StarIcon = ({ filled, halfFilled, clipId }: { filled: boolean; halfFilled: boolean; clipId?: string }) => {
  const starPath = 'M16 3l3.86 7.82L28 12.09l-6 5.84 1.42 8.27L16 22.26l-7.42 3.94L10 17.93 4 12.09l8.14-1.27L16 3z';

  if (halfFilled && clipId) {
    return (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <defs>
          <clipPath id={`${clipId}-left`}>
            <rect x="0" y="0" width="16" height="32" />
          </clipPath>
          <clipPath id={`${clipId}-right`}>
            <rect x="16" y="0" width="16" height="32" />
          </clipPath>
        </defs>
        <path d={starPath} className="fill-primary-600" clipPath={`url(#${clipId}-left)`} />
        <path d={starPath} className="fill-greyscale-400" clipPath={`url(#${clipId}-right)`} />
      </svg>
    );
  }

  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d={starPath} className={filled ? 'fill-primary-600' : 'fill-greyscale-400'} />
    </svg>
  );
};
